import MouseTrap from "mousetrap";
import { contains } from "underscore";

/**
 * @typedef {object}       AriaMenuOptions
 * @property {object}      callbacks
 * @property {function}    callbacks.onEscape           Action to execute when the user hits the
 *                                                      "escape" button
 * @property {function}    callbacks.onMenuButtonPress  Action to execute when the user presses the
 *                                                      button to open the menu
 * @property {function}    callbacks.onMenuFocusOut     Action to execute when the user tabs out
 *                                                      of the menu or the menu loses focus
 * @property {object}      elements
 * @property {HTMLElement} elements.menuButton          The button that opens up the menu
 * @property {HTMLElement} elements.menuContainer       The parent menu container element to be used
 *                                                      for binding keys to
 * @property {NodeList}    elements.menuItems           The individual menu items we want to be able
 *                                                      to iterate through by using the keyboard's
 *                                                      up/down left/right buttons.
 */

/**
 * Used to enable keyboard navigation in a menu for aria accessibility purposes.
 * Lets you use the keyboard's up/down left/right keys to go from one menu item to the next.
 */
export default class AriaMenu {
    menuButton: any;

    menuContainer: any;

    menuItems: any;

    lastFocusedElementIndex: any;

    menuItemsMouseTrap: any;

    openMenuButtonMouseTrap: any;

    onMenuButtonPress: any;

    onEscape: any;

    onMenuFocusOut: any;

    boundedMenuContainerFocusOutListener: any;

    /**
     * This assumes we have an html element that is the menu container, with child elements that are
     * the menuitem elements.
     *
     * @Example
     * <div role="menu">     <--- menu parent container
     *     <div role="menuitem">First menu action</div>     <-- menu item
     *     <div role="menuitem">Second menu action</div>    <-- menu item
     *     <div role="menuitem">Third menu action</div>     <-- menu item
     * </div>
     *
     * @param {AriaMenuOptions} ariaMenuOptions Options for aria menu
     */
    constructor(ariaMenuOptions) {
        /**
         * The button that opens up the menu when being clicked
         * @type {HTMLElement}
         * @private
         */
        this.menuButton = ariaMenuOptions.elements.menuButton;

        /**
         * The parent menu container element to be used for binding keys to
         * @type {HTMLElement}
         * @private
         */
        this.menuContainer = ariaMenuOptions.elements.menuContainer;

        /**
         * The individual menu items we want to be able to iterate through by
         * using the keyboard's up/down left/right buttons.
         * @type {NodeList}
         * @private
         */
        this.menuItems = ariaMenuOptions.elements.menuItems;

        /**
         * `number` is the index of the item that is focused starting at 0
         * `null` denotes not having any focus yet, so next item index that needs to be focused is 0
         * @type {number|null}
         * @private
         */
        this.lastFocusedElementIndex = null;

        /**
         * Keybinding library for when the focus is within the menu container
         * @type {Mousetrap}
         * @private
         */
        this.menuItemsMouseTrap = new MouseTrap(this.menuContainer);

        /**
         * Keybinding library for when the focus is on the button that opens the menu
         * @type {Mousetrap}
         * @private
         */
        this.openMenuButtonMouseTrap = new MouseTrap(this.menuButton);

        /**
         * Action to execute when the user presses enter/space on the open-menu button
         * @type {Function}
         */
        this.onMenuButtonPress = ariaMenuOptions.callbacks.onMenuButtonPress;

        /**
         * Action to execute when the user hits "escape" while browsing through the menu items
         * @type {Function}
         */
        this.onEscape = ariaMenuOptions.callbacks.onEscape;

        /**
         * Action to execute when the user tabs out of the menu or the menu loses focus, while the
         * menu is open
         * @type {Function}
         */
        this.onMenuFocusOut = ariaMenuOptions.callbacks.onMenuFocusOut;

        /**
         * The menuContainerFocusOutListener needs to be bound to `this`. However, when adding or
         * removing event listeners, you need to reference the same function. So we save the bounded
         * function here so both addEventListener and removeEventListener can reference the same
         * function.
         * @type {Function}
         */
        this.boundedMenuContainerFocusOutListener = this.menuContainerFocusOutListener.bind(this);

        this.setFocusLostAction(this.menuContainer);
        this.setKeyboardBindings();
    }

    /**
     * Clean up bindings
     */
    destroy() {
        this.unsetFocusLostAction(this.menuContainer);
        this.unsetKeyboardBindings();
    }

    /**
     * Focus one of the menu elements by its index
     * @param {number} index    the index of the element that needs to be focused
     */
    focusElementByIndex(index) {
        this.lastFocusedElementIndex = index;

        const elementToFocus = this.menuItems[this.lastFocusedElementIndex];
        elementToFocus.focus();
    }

    /**
     * Focus the next element, based on the currently focused element
     * Will loop around to the beginning if we're on the last element
     * @private
     */
    focusNext() {
        const noFocusYet = this.lastFocusedElementIndex === null;
        const lastElementIndex = this.menuItems.length - 1;
        const lastElementAlreadyFocused = this.lastFocusedElementIndex === lastElementIndex;

        if (noFocusYet || lastElementAlreadyFocused) {
            const firstElementIndex = 0;
            this.focusElementByIndex(firstElementIndex);
        } else {
            const nextElementIndex = this.lastFocusedElementIndex + 1;
            this.focusElementByIndex(nextElementIndex);
        }
    }

    /**
     * Focus the previous element, based on the currently focused element
     * Will loop around to the end if we're on the first element
     * @private
     */
    focusPrevious() {
        const noFocusYet = this.lastFocusedElementIndex === null;
        const firstElementAlreadyFocused = this.lastFocusedElementIndex === 0;

        if (noFocusYet || firstElementAlreadyFocused) {
            const lastElementIndex = this.menuItems.length - 1;
            this.focusElementByIndex(lastElementIndex);
        } else {
            const previousElementIndex = this.lastFocusedElementIndex - 1;
            this.focusElementByIndex(previousElementIndex);
        }
    }

    /**
     * @param {FocusEvent} focusEvent  Focus out event, when somebody is focusing out of a menu item
     */
    menuContainerFocusOutListener(focusEvent) {
        const newElementBeingFocused = focusEvent.relatedTarget;
        const menuFocussableItems = [...this.menuItems];
        const userFocusedAnotherMenuItem = contains(menuFocussableItems, newElementBeingFocused);

        if (!userFocusedAnotherMenuItem) {
            this.onMenuFocusOut();
        }
    }

    /**
     * @param {HTMLElement} menuContainer   The container which holds the different menu items
     */
    setFocusLostAction(menuContainer) {
        menuContainer.addEventListener("focusout", this.boundedMenuContainerFocusOutListener);
    }

    /**
     * Bind keyboard keys to different actions
     * @private
     */
    setKeyboardBindings() {
        /**
         * Bindings for when the focus is on one of the menu items
         */
        this.menuItemsMouseTrap.bind(["down", "right"], keyboardEvent => {
            this.focusNext();

            // In mousetrap, returning false preventsDefault and stops propagation
            return false;
        });

        this.menuItemsMouseTrap.bind(["up", "left"], keyboardEvent => {
            this.focusPrevious();

            // In mousetrap, returning false preventsDefault and stops propagation
            return false;
        });

        this.menuItemsMouseTrap.bind(["enter", "space"], keyboardEvent => {
            const currentlyFocusedElement = this.menuItems[this.lastFocusedElementIndex];
            currentlyFocusedElement.click();

            // In mousetrap, returning false preventsDefault and stops propagation
            return false;
        });

        this.menuItemsMouseTrap.bind("escape", keyboardEvent => {
            this.onEscape(keyboardEvent);
            this.menuButton.focus();

            // In mousetrap, returning false preventsDefault and stops propagation
            return false;
        });

        /**
         * Bindings for when the focus is on the open-menu button
         */
        this.openMenuButtonMouseTrap.bind(["enter", "space"], keyboardEvent => {
            this.onMenuButtonPress(keyboardEvent);

            // In mousetrap, returning false preventsDefault and stops propagation
            return false;
        });
    }

    /**
     * @param {HTMLElement} menuContainer   The container which holds the different menu items
     */
    unsetFocusLostAction(menuContainer) {
        menuContainer.removeEventListener("focusout", this.boundedMenuContainerFocusOutListener);
    }

    /**
     * Remove keyboard bindings
     * @private
     */
    unsetKeyboardBindings() {
        this.menuItemsMouseTrap.reset();
        this.openMenuButtonMouseTrap.reset();
    }
}
